import { useEffect, useRef, useCallback, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useNavigate } from 'react-router-dom';
import { useMovieStore, useAuthStore } from '../stores';
import { refreshAccessToken } from '../util/tokenRefresh';
import { resolveAssetUrl } from '../util/api';
import { extractAssetPathFromBody } from '../util/imageBody';
import type {
  Movie,
  Scene,
  SceneObject,
  LibraryItem,
  MovieResponse,
  SceneResponse,
  SceneObjectResponse,
  SceneObjectLabelUpdateRequest,
  SceneObjectMoveToLayerRequest,
  SceneObjectHierarchyUpdateRequest,
  SceneObjectPivotUpdateRequest,
  SceneObjectVisibilityUpdateRequest,
  SceneObjectLockUpdateRequest,
  ResortSceneObjectsRequest,
  SceneObjectContentUpdateRequest,
  SceneObjectStateResponse,
  PatchStatePropertiesRequest,
  SceneEffectResponse,
  PatchSceneEffectPropertiesRequest,
  LibraryObjectResponse,
  TimelineLayerResponse,
  KeyFrameResponse,
  TweenResponse,
  ClipResponse,
  TimelineSequenceResponse,
} from '../types';

// ============================================================================
// Connection status type
// ============================================================================

export type ConnectionStatus = 'Disconnected' | 'Connecting' | 'Connected' | 'Reconnecting' | 'Error';

// ============================================================================
// Hook return type
// ============================================================================

export interface SignalRConnection {
  // Connection state
  status: ConnectionStatus;
  error: string | null;

  // Connection management
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;

  // Movie operations
  moviesGetAll: () => Promise<MovieResponse[]>;
  moviesGetByName: (name: string) => Promise<MovieResponse[]>;
  movieSave: (movie: MovieResponse) => Promise<MovieResponse>;

  // Scene operations
  scenesOfMovie: (movie: MovieResponse) => Promise<SceneResponse[]>;
  sceneSave: (scene: SceneResponse) => Promise<SceneResponse>;
  sceneCreate: (scene: SceneResponse) => Promise<SceneResponse>;
  sceneDelete: (scene: SceneResponse) => Promise<void>;
  scenesResort: (scenes: SceneResponse[]) => Promise<void>;

  // Library operations
  libraryObjects: (movie: MovieResponse) => Promise<LibraryObjectResponse[]>;
  libraryObjectSave: (item: LibraryObjectResponse) => Promise<LibraryObjectResponse>;
  libraryObjectGet: (id: number) => Promise<LibraryObjectResponse>;
  libraryObjectDelete: (id: number) => Promise<void>;
  libraryObjectFilePath: (item: LibraryObjectResponse) => Promise<string>;

  // SceneObject operations
  sceneObjectsGet: (scene: SceneResponse) => Promise<SceneObjectResponse[]>;
  sceneObjectsGetByLayer: (scene: SceneResponse) => Promise<TimelineLayerResponse[]>;
  sceneObjectCreateFromLibraryItem: (libraryObject: LibraryObjectResponse, sceneId: number, layerId: number) => Promise<SceneObjectResponse>;
  sceneObjectSave: (sceneObject: SceneObjectResponse) => Promise<SceneObjectResponse>;
  sceneObjectUpdateLabel: (request: SceneObjectLabelUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectMoveToLayer: (request: SceneObjectMoveToLayerRequest) => Promise<SceneObjectResponse>;
  sceneObjectUpdateHierarchy: (request: SceneObjectHierarchyUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectUpdatePivot: (request: SceneObjectPivotUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectSetVisibility: (request: SceneObjectVisibilityUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectSetLock: (request: SceneObjectLockUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectsResort: (request: ResortSceneObjectsRequest) => Promise<void>;
  sceneObjectUpdateContent: (request: SceneObjectContentUpdateRequest) => Promise<SceneObjectResponse>;
  sceneObjectsSave: (sceneObjects: SceneObjectResponse[]) => Promise<void>;
  sceneObjectDelete: (sceneObject: SceneObjectResponse) => Promise<void>;
  sceneObjectDeleteBatch: (sceneObjects: SceneObjectResponse[]) => Promise<void>;
  sceneObjectGetState: (sceneObject: SceneObjectResponse, keyFrameId: number) => Promise<SceneObjectStateResponse | null>;
  sceneObjectSaveState: (state: SceneObjectStateResponse) => Promise<SceneObjectStateResponse>;
  sceneObjectSaveStatesBatch: (states: SceneObjectStateResponse[]) => Promise<void>;
  sceneObjectPatchStateProperties: (request: PatchStatePropertiesRequest) => Promise<SceneObjectStateResponse>;

  // SceneEffect operations
  sceneEffectsGet: (scene: SceneResponse) => Promise<SceneEffectResponse[]>;
  sceneEffectSave: (request: SceneEffectResponse) => Promise<SceneEffectResponse>;
  sceneEffectUpsertByType: (request: SceneEffectResponse) => Promise<SceneEffectResponse>;
  sceneEffectPatchProperties: (request: PatchSceneEffectPropertiesRequest) => Promise<SceneEffectResponse>;
  sceneEffectDelete: (effect: SceneEffectResponse) => Promise<void>;

  // Timeline Layer operations
  timelineLayersOfScene: (scene: SceneResponse) => Promise<TimelineLayerResponse[]>;
  timelineLayerCreate: (name: string, scene: SceneResponse, sorter: number) => Promise<TimelineLayerResponse>;
  timelineLayerSave: (layer: TimelineLayerResponse) => Promise<TimelineLayerResponse>;
  timelineLayerSaveBatch: (layers: TimelineLayerResponse[]) => Promise<void>;
  timelineLayerDelete: (layer: TimelineLayerResponse) => Promise<void>;

  // Timeline sequence (for playback)
  timelineReadSceneSequence: (scene: SceneResponse, fps: number) => Promise<TimelineSequenceResponse>;
  timelineReadMovieSequence: (movie: MovieResponse) => Promise<TimelineSequenceResponse[]>;

  // KeyFrame operations
  keyFrameSave: (keyFrame: KeyFrameResponse) => Promise<KeyFrameResponse>;
  keyFrameDelete: (keyFrame: KeyFrameResponse) => Promise<void>;

  // Tween operations
  tweenSave: (tween: TweenResponse) => Promise<TweenResponse>;
  tweenDelete: (tween: TweenResponse) => Promise<void>;

  // Clip operations
  clipCreate: (sceneObjectId: number) => Promise<ClipResponse>;
  clipUpdate: (clip: ClipResponse) => Promise<ClipResponse>;
  clipSetRootSceneObject: (clip: ClipResponse, sceneObjectId: number) => Promise<ClipResponse>;
}

// ============================================================================
// Configuration
// ============================================================================

const MOVIE_HUB_URL = '/movieHub';
const TIMELINE_HUB_URL = '/timelineHub';
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000]; // Reconnect backoff

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an error indicates unauthorized access (401/403)
 */
function isUnauthorizedError(err: unknown): boolean {
  if (!err) return false;
  
  const message = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  
  // Check for common unauthorized error patterns
  return (
    message.includes('unauthorized') ||
    message.includes('401') ||
    message.includes('403') ||
    message.includes('forbidden') ||
    message.includes('not authorized') ||
    message.includes('authentication failed') ||
    message.includes('token expired') ||
    message.includes('invalid token')
  );
}

function normalizeObjectSize(value: number | undefined): number {
  return typeof value === 'number' && value > 0 ? Math.round(value) : 100;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSignalR(autoConnect = true): SignalRConnection {
  const movieConnectionRef = useRef<HubConnection | null>(null);
  const timelineConnectionRef = useRef<HubConnection | null>(null);
  const handleAuthFailureRef = useRef<(() => Promise<void>) | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('Disconnected');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const accessToken = useAuthStore((state) => state.accessToken);
  const logout = useAuthStore((state) => state.logout);

  // Get store actions for syncing (used in server-to-client event handlers)
  const { addSceneObject, updateSceneObject, deleteSceneObject } = useMovieStore();

  const createConnection = useCallback((hubUrl: string, hubName: string) => {
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => useAuthStore.getState().accessToken || '',
      })
      .withAutomaticReconnect(RECONNECT_DELAYS)
      .configureLogging(LogLevel.Information)
      .build();

    // Connection lifecycle events
    connection.onreconnecting((err) => {
      console.log(`[SignalR:${hubName}] Reconnecting...`, err);
      setStatus('Reconnecting');
      setError(err?.message ?? null);
    });

    connection.onreconnected((connectionId) => {
      console.log(`[SignalR:${hubName}] Reconnected:`, connectionId);
      setStatus('Connected');
      setError(null);
    });

    connection.onclose((err) => {
      console.log(`[SignalR:${hubName}] Connection closed`, err);
      setStatus('Disconnected');
      if (err) {
        setError(err.message);
        // Check if the error is due to unauthorized access
        if (isUnauthorizedError(err)) {
          console.warn('[SignalR] Unauthorized error detected, attempting token refresh...');
          void handleAuthFailureRef.current?.();
        }
      }
    });

    // Server-to-client events

    // Connection lifecycle event
    connection.on('Connected', (data: { message: string; connectionId: string; userId: string; timestamp: string }) => {
      console.log(`[SignalR:${hubName}] Connected:`, data);
      console.log(`✅ ${data.message}`);
      console.log(`   Connection ID: ${data.connectionId}`);
      console.log(`   User ID: ${data.userId}`);
    });

    if (hubName === 'MovieHub') {
      // Real-time collaboration events (if backend broadcasts)
      connection.on('SceneObjectUpdated', (sceneObject: SceneObjectResponse) => {
        console.log('[SignalR] SceneObjectUpdated', sceneObject);
        // Convert and update store
        updateSceneObject(String(sceneObject.id), {
          x: sceneObject.dropPosition?.left ?? 0,
          y: sceneObject.dropPosition?.top ?? 0,
          visible: sceneObject.isVisible,
          locked: sceneObject.isLocked,
        });
      });

      connection.on('SceneObjectCreated', (sceneObject: SceneObjectResponse) => {
        console.log('[SignalR] SceneObjectCreated', sceneObject);
        addSceneObject(convertSceneObjectResponse(sceneObject));
      });

      connection.on('SceneObjectDeleted', (sceneObject: SceneObjectResponse) => {
        console.log('[SignalR] SceneObjectDeleted', sceneObject);
        deleteSceneObject(String(sceneObject.id));
      });
    }

    return connection;
  }, [updateSceneObject, addSceneObject, deleteSceneObject]);

  const waitForDisconnected = useCallback(async () => {
    const maxWaitMs = 2000;
    const start = Date.now();

    const refs = [movieConnectionRef, timelineConnectionRef];
    while (refs.some((r) => r.current && r.current.state === HubConnectionState.Disconnecting)) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (Date.now() - start > maxWaitMs) {
        break;
      }
    }
  }, []);

  // Connect
  const connect = useCallback(async () => {
    if (
      movieConnectionRef.current?.state === HubConnectionState.Disconnecting ||
      timelineConnectionRef.current?.state === HubConnectionState.Disconnecting
    ) {
      console.log('[SignalR] Waiting for disconnect to complete...');
      await waitForDisconnected();
    }

    const movieState = movieConnectionRef.current?.state;
    const timelineState = timelineConnectionRef.current?.state;
    const movieReady = !movieState || movieState === HubConnectionState.Disconnected;
    const timelineReady = !timelineState || timelineState === HubConnectionState.Disconnected;

    // Only proceed if we're not already trying to connect or already connected
    if (!movieReady || !timelineReady) {
      console.log('[SignalR] Connection attempt skipped: current states are', { movieState, timelineState });
      return;
    }

    try {
      setStatus('Connecting');
      setError(null);

      if (!movieConnectionRef.current) {
        movieConnectionRef.current = createConnection(MOVIE_HUB_URL, 'MovieHub');
      }
      if (!timelineConnectionRef.current) {
        timelineConnectionRef.current = createConnection(TIMELINE_HUB_URL, 'TimelineHub');
      }

      await movieConnectionRef.current.start();
      await timelineConnectionRef.current.start();
      console.log('[SignalR] Connected (MovieHub + TimelineHub)');
      setStatus('Connected');
    } catch (err) {
      // If the connection was stopped during negotiation (e.g. by a rapid unmount in Strict Mode),
      // we can ignore this error as it's an expected part of the React lifecycle.
      const abortMessage = err instanceof Error ? err.message : String(err);
      if (err instanceof Error && (err.name === 'AbortError' || abortMessage.includes('stopped during negotiation'))) {
        console.log('[SignalR] Connection aborted during negotiation (expected during unmount)');
        return;
      }

      console.error('[SignalR] Connection failed:', err);
      setStatus('Error');
      setError(err instanceof Error ? err.message : 'Connection failed');
      
      // Check if the error is due to unauthorized access
      if (err && isUnauthorizedError(err)) {
        console.warn('[SignalR] Unauthorized error detected, attempting token refresh...');
        void handleAuthFailureRef.current?.();
      }
    }
  }, [createConnection, waitForDisconnected]);

  // Disconnect
  const disconnect = useCallback(async () => {
    const movie = movieConnectionRef.current;
    const timeline = timelineConnectionRef.current;

    if (movie) await movie.stop();
    if (timeline) await timeline.stop();

    movieConnectionRef.current = null;
    timelineConnectionRef.current = null;
    setStatus('Disconnected');
  }, []);

  // Helper to invoke hub methods safely
  const invokeMovie = useCallback(async <T>(method: string, ...args: unknown[]): Promise<T> => {
    if (!movieConnectionRef.current || movieConnectionRef.current.state !== HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }
    try {
      return await movieConnectionRef.current.invoke<T>(method, ...args);
    } catch (err) {
      // Check if the error is due to unauthorized access
      if (isUnauthorizedError(err)) {
        console.warn('[SignalR] Unauthorized error during invoke, attempting token refresh...');
        void handleAuthFailureRef.current?.();
      }
      throw err;
    }
  }, []);

  const invokeTimeline = useCallback(async <T>(method: string, ...args: unknown[]): Promise<T> => {
    if (!timelineConnectionRef.current || timelineConnectionRef.current.state !== HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }
    try {
      return await timelineConnectionRef.current.invoke<T>(method, ...args);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        console.warn('[SignalR] Unauthorized error during invoke, attempting token refresh...');
        void handleAuthFailureRef.current?.();
      }
      throw err;
    }
  }, []);

  const handleAuthFailure = useCallback(async () => {
    const newToken = await refreshAccessToken();
    if (newToken) {
      await disconnect();
      await connect();
      return;
    }

    logout();
    navigate('/login', { replace: true });
  }, [connect, disconnect, logout, navigate]);

  handleAuthFailureRef.current = handleAuthFailure;

  const previousAccessTokenRef = useRef(accessToken);

  useEffect(() => {
    const previousToken = previousAccessTokenRef.current;
    previousAccessTokenRef.current = accessToken;

    if (
      !autoConnect ||
      !previousToken ||
      !accessToken ||
      previousToken === accessToken ||
      (status !== 'Connected' && status !== 'Reconnecting')
    ) {
      return;
    }

    void (async () => {
      await disconnect();
      await connect();
    })();
  }, [accessToken, autoConnect, status, connect, disconnect]);

  // Auto-connect on mount
  useEffect(() => {
    if (!autoConnect) {
      return () => {
        disconnect();
      };
    }

    const timer = setTimeout(() => {
      connect();
    }, 0);

    return () => {
      clearTimeout(timer);
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // ============================================================================
  // Hub Method Wrappers
  // ============================================================================

  // Movie operations
  const moviesGetAll = useCallback(() => invokeMovie<MovieResponse[]>('MoviesGetAll'), [invokeMovie]);
  const moviesGetByName = useCallback((name: string) => invokeMovie<MovieResponse[]>('MoviesGetByName', name), [invokeMovie]);
  const movieSave = useCallback((movie: MovieResponse) => invokeMovie<MovieResponse>('MovieSaveData', movie), [invokeMovie]);

  // Scene operations
  const scenesOfMovie = useCallback((movie: MovieResponse) => invokeMovie<SceneResponse[]>('ScenesOfMovie', movie), [invokeMovie]);
  const sceneSave = useCallback((scene: SceneResponse) => invokeMovie<SceneResponse>('SceneSaveData', scene), [invokeMovie]);
  const sceneCreate = useCallback((scene: SceneResponse) => invokeMovie<SceneResponse>('SceneCreate', scene), [invokeMovie]);
  const sceneDelete = useCallback((scene: SceneResponse) => invokeMovie<void>('SceneDelete', scene), [invokeMovie]);
  const scenesResort = useCallback((scenes: SceneResponse[]) => invokeMovie<void>('ScenesResort', scenes), [invokeMovie]);

  // Library operations
  const libraryObjects = useCallback((movie: MovieResponse) => invokeMovie<LibraryObjectResponse[]>('LibraryObjects', movie), [invokeMovie]);
  const libraryObjectSave = useCallback((item: LibraryObjectResponse) => invokeMovie<LibraryObjectResponse>('LibraryObjectSave', item), [invokeMovie]);
  const libraryObjectGet = useCallback((id: number) => invokeMovie<LibraryObjectResponse>('LibraryObjectGet', id), [invokeMovie]);
  const libraryObjectDelete = useCallback((id: number) => invokeMovie<void>('LibraryObjectDelete', id), [invokeMovie]);
  const libraryObjectFilePath = useCallback((item: LibraryObjectResponse) => invokeMovie<string>('LibraryObjectFilePath', item), [invokeMovie]);

  // SceneObject operations
  const sceneObjectsGet = useCallback((scene: SceneResponse) => invokeMovie<SceneObjectResponse[]>('SceneObjectsGet', scene), [invokeMovie]);
  const sceneObjectsGetByLayer = useCallback((scene: SceneResponse) => invokeMovie<TimelineLayerResponse[]>('SceneObjectsGetByLayer', scene), [invokeMovie]);
  const sceneObjectCreateFromLibraryItem = useCallback(
    (libraryObject: LibraryObjectResponse, sceneId: number, layerId: number) =>
      invokeMovie<SceneObjectResponse>('SceneObjectCreateFromLibraryItem', libraryObject, sceneId, layerId),
    [invokeMovie]
  );
  const sceneObjectSave = useCallback((sceneObject: SceneObjectResponse) => invokeMovie<SceneObjectResponse>('SceneObjectSave', sceneObject), [invokeMovie]);
  const sceneObjectUpdateLabel = useCallback(
    (request: SceneObjectLabelUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectUpdateLabel', request),
    [invokeMovie]
  );
  const sceneObjectMoveToLayer = useCallback(
    (request: SceneObjectMoveToLayerRequest) => invokeMovie<SceneObjectResponse>('SceneObjectMoveToLayer', request),
    [invokeMovie]
  );
  const sceneObjectUpdateHierarchy = useCallback(
    (request: SceneObjectHierarchyUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectUpdateHierarchy', request),
    [invokeMovie]
  );
  const sceneObjectUpdatePivot = useCallback(
    (request: SceneObjectPivotUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectUpdatePivot', request),
    [invokeMovie]
  );
  const sceneObjectSetVisibility = useCallback(
    (request: SceneObjectVisibilityUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectSetVisibility', request),
    [invokeMovie]
  );
  const sceneObjectSetLock = useCallback(
    (request: SceneObjectLockUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectSetLock', request),
    [invokeMovie]
  );
  const sceneObjectsResort = useCallback(
    (request: ResortSceneObjectsRequest) => invokeMovie<void>('SceneObjectsResort', request),
    [invokeMovie]
  );
  const sceneObjectUpdateContent = useCallback(
    (request: SceneObjectContentUpdateRequest) => invokeMovie<SceneObjectResponse>('SceneObjectUpdateContent', request),
    [invokeMovie]
  );
  const sceneObjectsSave = useCallback((sceneObjects: SceneObjectResponse[]) => invokeMovie<void>('SceneObjectsSave', sceneObjects), [invokeMovie]);
  const sceneObjectDelete = useCallback((sceneObject: SceneObjectResponse) => invokeMovie<void>('SceneObjectDelete', sceneObject), [invokeMovie]);
  const sceneObjectDeleteBatch = useCallback((sceneObjects: SceneObjectResponse[]) => invokeMovie<void>('SceneObjectDeleteBatch', sceneObjects), [invokeMovie]);
  const sceneObjectGetState = useCallback(
    (sceneObject: SceneObjectResponse, keyFrameId: number) =>
      invokeMovie<SceneObjectStateResponse | null>('SceneObjectGetState', sceneObject, keyFrameId),
    [invokeMovie]
  );
  const sceneObjectSaveState = useCallback((state: SceneObjectStateResponse) => invokeMovie<SceneObjectStateResponse>('SceneObjectSaveState', state), [invokeMovie]);
  const sceneObjectSaveStatesBatch = useCallback((states: SceneObjectStateResponse[]) => invokeMovie<void>('SceneObjectSaveStatesBatch', states), [invokeMovie]);
  const sceneObjectPatchStateProperties = useCallback(
    (request: PatchStatePropertiesRequest) => invokeMovie<SceneObjectStateResponse>('SceneObjectPatchStateProperties', request),
    [invokeMovie]
  );

  const sceneEffectsGet = useCallback(
    (scene: SceneResponse) => invokeMovie<SceneEffectResponse[]>('SceneEffectsGet', scene),
    [invokeMovie]
  );
  const sceneEffectSave = useCallback(
    (request: SceneEffectResponse) => invokeMovie<SceneEffectResponse>('SceneEffectSave', request),
    [invokeMovie]
  );
  const sceneEffectUpsertByType = useCallback(
    (request: SceneEffectResponse) => invokeMovie<SceneEffectResponse>('SceneEffectUpsertByType', request),
    [invokeMovie]
  );
  const sceneEffectPatchProperties = useCallback(
    (request: PatchSceneEffectPropertiesRequest) => invokeMovie<SceneEffectResponse>('SceneEffectPatchProperties', request),
    [invokeMovie]
  );
  const sceneEffectDelete = useCallback(
    (effect: SceneEffectResponse) => invokeMovie<void>('SceneEffectDelete', effect),
    [invokeMovie]
  );

  // Timeline Layer operations
  const timelineLayersOfScene = useCallback((scene: SceneResponse) => invokeTimeline<TimelineLayerResponse[]>('TimelineLayersOfScene', scene), [invokeTimeline]);
  const timelineLayerCreate = useCallback(
    (name: string, scene: SceneResponse, sorter: number) => invokeTimeline<TimelineLayerResponse>('TimelineLayerCreate', name, scene, sorter),
    [invokeTimeline]
  );
  const timelineLayerSave = useCallback((layer: TimelineLayerResponse) => invokeTimeline<TimelineLayerResponse>('TimelineLayerSave', layer), [invokeTimeline]);
  const timelineLayerSaveBatch = useCallback((layers: TimelineLayerResponse[]) => invokeTimeline<void>('TimelineLayerSaveBatch', layers), [invokeTimeline]);
  const timelineLayerDelete = useCallback((layer: TimelineLayerResponse) => invokeTimeline<void>('TimelineLayerDelete', layer), [invokeTimeline]);

  // Timeline sequence
  const timelineReadSceneSequence = useCallback(
    (scene: SceneResponse, fps: number) => invokeTimeline<TimelineSequenceResponse>('TimelineReadSceneSequence', scene, fps),
    [invokeTimeline]
  );
  const timelineReadMovieSequence = useCallback((movie: MovieResponse) => invokeTimeline<TimelineSequenceResponse[]>('TimelineReadMovieSequence', movie), [invokeTimeline]);

  // KeyFrame operations
  const keyFrameSave = useCallback((keyFrame: KeyFrameResponse) => invokeTimeline<KeyFrameResponse>('KeyFrameSave', keyFrame), [invokeTimeline]);
  const keyFrameDelete = useCallback((keyFrame: KeyFrameResponse) => invokeTimeline<void>('KeyFrameDelete', keyFrame), [invokeTimeline]);

  // Tween operations
  const tweenSave = useCallback((tween: TweenResponse) => invokeTimeline<TweenResponse>('TweenSave', tween), [invokeTimeline]);
  const tweenDelete = useCallback((tween: TweenResponse) => invokeTimeline<void>('TweenDelete', tween), [invokeTimeline]);

  // Clip operations
  const clipCreate = useCallback((sceneObjectId: number) => invokeMovie<ClipResponse>('ClipCreate', sceneObjectId), [invokeMovie]);
  const clipUpdate = useCallback((clip: ClipResponse) => invokeMovie<ClipResponse>('ClipUpdate', clip), [invokeMovie]);
  const clipSetRootSceneObject = useCallback(
    (clip: ClipResponse, sceneObjectId: number) => invokeMovie<ClipResponse>('ClipSetRootSceneObject', clip, sceneObjectId),
    [invokeMovie]
  );

  return {
    status,
    error,
    connect,
    disconnect,

    // Movie
    moviesGetAll,
    moviesGetByName,
    movieSave,

    // Scene
    scenesOfMovie,
    sceneSave,
    sceneCreate,
    sceneDelete,
    scenesResort,

    // Library
    libraryObjects,
    libraryObjectSave,
    libraryObjectGet,
    libraryObjectDelete,
    libraryObjectFilePath,

    // SceneObject
    sceneObjectsGet,
    sceneObjectsGetByLayer,
    sceneObjectCreateFromLibraryItem,
    sceneObjectSave,
    sceneObjectUpdateLabel,
    sceneObjectMoveToLayer,
    sceneObjectUpdateHierarchy,
    sceneObjectUpdatePivot,
    sceneObjectSetVisibility,
    sceneObjectSetLock,
    sceneObjectsResort,
    sceneObjectUpdateContent,
    sceneObjectsSave,
    sceneObjectDelete,
    sceneObjectDeleteBatch,
    sceneObjectGetState,
    sceneObjectSaveState,
    sceneObjectSaveStatesBatch,
    sceneObjectPatchStateProperties,

    sceneEffectsGet,
    sceneEffectSave,
    sceneEffectUpsertByType,
    sceneEffectPatchProperties,
    sceneEffectDelete,

    // Timeline Layer
    timelineLayersOfScene,
    timelineLayerCreate,
    timelineLayerSave,
    timelineLayerSaveBatch,
    timelineLayerDelete,
    timelineReadSceneSequence,
    timelineReadMovieSequence,

    // KeyFrame
    keyFrameSave, //create+move 
    keyFrameDelete,

    // Tween
    tweenSave, //create+move 
    tweenDelete,

    // Clip
    clipCreate,
    clipUpdate,
    clipSetRootSceneObject,
  };
}

// ============================================================================
// Conversion Helpers (Response DTOs → Frontend UI types)
// ============================================================================

/** Convert SceneObjectResponse to frontend SceneObject for UI rendering */
function convertSceneObjectResponse(response: SceneObjectResponse): SceneObject {
  const defaultState = response.defaultState;
  let props: Record<string, unknown> = {};

  if (defaultState?.properties) {
    try {
      props = JSON.parse(defaultState.properties);
    } catch {
      // Invalid JSON, use defaults
    }
  }

  const rawSourcePath =
    response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
  const resolvedSrc = rawSourcePath ? resolveAssetUrl(rawSourcePath) : '';

  const x = Math.round((props.left as number) ?? (props.x as number) ?? response.dropPosition?.left ?? 0);
  const y = Math.round((props.top as number) ?? (props.y as number) ?? response.dropPosition?.top ?? 0);
  const width = normalizeObjectSize((props.width as number) ?? response.width);
  const height = normalizeObjectSize((props.height as number) ?? response.height);
  const rotation = (props.rotation as number) ?? 0;
  const opacity = (props.opacity as number) ?? 1;

  return {
    id: String(response.id),
    type: (response.type as SceneObject['type']) || 'image',
    name: response.name,
    src: resolvedSrc || undefined,
    libraryObjectId: response.libraryObjectId ?? undefined,
    parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
    isGroup: response.isGroup === true || response.type === 'group',
    x,
    y,
    width,
    height,
    rotation,
    opacity,
    zIndex: response.sorter ?? 0,
    visible: response.isVisible !== false,
    locked: response.isLocked === true,
    pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
    layerId: response.layerId,
    defaultPose: { x, y, width, height, rotation, opacity },
    states: [],
  };
}

/** Convert MovieResponse to frontend Movie for UI rendering */
export function convertMovieResponse(response: MovieResponse): Movie {
  const width = response.width > 0 ? response.width : 500;
  const height = response.height > 0 ? response.height : 500;

  return {
    id: String(response.id),
    name: response.name,
    description: response.description ?? '',
    notes: '',
    width,
    height,
    fps: response.fps,
    scenes: response.scenes.map(convertSceneResponse),
  };
}

/** Convert SceneResponse to frontend Scene for UI rendering */
export function convertSceneResponse(response: SceneResponse): Scene {
  return {
    id: String(response.id),
    name: response.name,
    order: response.sorter,
    objects: [],
  };
}

/** Convert LibraryObjectResponse to frontend LibraryItem for UI rendering */
export function convertLibraryObjectResponse(response: LibraryObjectResponse): LibraryItem {
  const src = response.url
    ? resolveAssetUrl(response.url)
    : response.fullPath
      ? resolveAssetUrl(response.fullPath)
      : '';
  const thumbnail = response.thumbnailUrl ? resolveAssetUrl(response.thumbnailUrl) : undefined;
  return {
    id: String(response.id),
    name: response.name,
    type: response.type as 'image' | 'clip',
    src,
    thumbnail,
  };
}

// ============================================================================
// Export default for convenience
// ============================================================================

export default useSignalR;
